/**
 * Espace client — Mes rendez-vous. Le client demande un RDV pour un dossier ;
 * BCL lui proposera des créneaux par email.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  appointmentStatusBadge,
  formatSlot,
} from '@/features/appointments/appointment-labels';
import {
  chooseAppointmentSlot,
  listMyAppointments,
  requestAppointment,
} from '@/features/appointments/appointments-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { listProjects } from '@/features/projects/projects-api';

export function DashboardRendezVousPage() {
  const qc = useQueryClient();
  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });
  const appointments = useQuery({
    queryKey: ['appointments', 'mine'],
    queryFn: listMyAppointments,
  });

  const [projectId, setProjectId] = useState('');
  const [message, setMessage] = useState('');

  const requestMut = useMutation({
    mutationFn: () =>
      requestAppointment({ projectId, ...(message ? { message } : {}) }),
    onSuccess: () => {
      toast.success('Demande envoyée, BCL vous proposera des créneaux');
      qc.invalidateQueries({ queryKey: ['appointments', 'mine'] });
      setProjectId('');
      setMessage('');
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const chooseMut = useMutation({
    mutationFn: ({ id, slotIndex }: { id: string; slotIndex: number }) =>
      chooseAppointmentSlot(id, slotIndex),
    onSuccess: () => {
      toast.success('Créneau confirmé');
      qc.invalidateQueries({ queryKey: ['appointments', 'mine'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <DashboardLayout variant="client" title="Mes rendez-vous">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Mes rendez-vous</CardTitle>
            <CardDescription>
              {appointments.data
                ? `${appointments.data.length} rendez-vous`
                : 'Chargement…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dossier</TableHead>
                  <TableHead>Créneau</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Choix du créneau</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appointments.data?.length ? (
                  appointments.data.map((rdv) => (
                    <TableRow key={rdv.id}>
                      <TableCell className="font-medium">
                        {rdv.projectReference ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatSlot(rdv.confirmedSlot)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={appointmentStatusBadge[rdv.status].cls}
                        >
                          {appointmentStatusBadge[rdv.status].label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {rdv.status === 'creneaux_proposes' ? (
                          <div className="flex flex-col gap-1">
                            {rdv.proposedSlots.map((slot, index) => (
                              <Button
                                key={slot}
                                size="sm"
                                variant="outline"
                                disabled={chooseMut.isPending}
                                onClick={() =>
                                  chooseMut.mutate({ id: rdv.id, slotIndex: index })
                                }
                              >
                                {formatSlot(slot)}
                              </Button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                      Aucun rendez-vous.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5" />
              Demander un rendez-vous
            </CardTitle>
            <CardDescription>BCL vous proposera deux créneaux par email.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="projectId">Dossier concerné</Label>
              <select
                id="projectId"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">— Choisir —</option>
                {projects.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.reference}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="message">Message (optionnel)</Label>
              <Input
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Disponibilités, précisions…"
              />
            </div>
            <Button
              className="w-full"
              disabled={!projectId || requestMut.isPending}
              onClick={() => requestMut.mutate()}
            >
              {requestMut.isPending ? 'Envoi…' : 'Demander un rendez-vous'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
